import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import VerifiedIcon from "@mui/icons-material/Verified";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import api from "../../../../conf/api";
import VideoPlayer from "../VideoPlayer";
import { ApprovalBadge, TimeAgo, WcaBadge } from "./constants";

/** Below sm the big dialogs take the whole screen instead of a cramped sheet. */
const useIsPhone = () => useMediaQuery((theme) => theme.breakpoints.down("sm"));

// Confirm dialogs stay as sheets, but their buttons must never run off the
// edge — they wrap and stay full-width-ish on a phone.
const CONFIRM_ACTIONS_SX = {
  px: { xs: 2, sm: 3 },
  pb: { xs: 2, sm: 2.5 },
  gap: 1,
  flexWrap: "wrap",
  "& .MuiButton-root": { minHeight: 40 },
};

// ── Manage the WGIDs attached to an existing compilation ─────────────────────
export function ManageWgidsDialog({ open, compilation, onClose, onChanged }) {
  const fullScreen = useIsPhone();
  const [wgidInput, setWgidInput] = useState("");
  const [attaching, setAttaching] = useState(false);
  const [wgids, setWgids] = useState(compilation?.wgids || []);

  useEffect(() => {
    setWgids(compilation?.wgids || []);
  }, [compilation]);

  if (!compilation) return null;

  // Accepts one WGID or several comma-separated at once — each verified and
  // attached concurrently against the backend.
  const handleAttach = async () => {
    const candidates = Array.from(new Set(wgidInput.split(",").map((w) => w.trim()).filter(Boolean)));
    if (candidates.length === 0) return;

    setAttaching(true);
    try {
      const results = await Promise.all(
        candidates.map(async (wgid) => {
          try {
            const r = await api.post(`/mrss/compilations/${compilation.id}/attach-wgid/`, { wgid });
            return { ok: true, wgid, data: r.data };
          } catch (err) {
            return { ok: false, wgid, error: err.response?.data?.error };
          }
        })
      );
      const attached = results.filter((r) => r.ok);
      const failed = results.filter((r) => !r.ok);
      if (attached.length > 0) {
        setWgids((prev) => [
          ...prev.filter((w) => !attached.some((a) => a.wgid === w.wgid)),
          ...attached.map(({ data }) => ({
            wgid: data.wgid, verified: data.verified, verified_title: data.verified_title, verified_at: data.verified_at,
          })),
        ]);
        toast.success(
          attached.length === 1
            ? `WGID ${attached[0].wgid} verified and attached`
            : `${attached.length} WGIDs verified and attached`
        );
        onChanged && onChanged();
      }
      if (failed.length > 0) toast.error(`Failed: ${failed.map((f) => f.wgid).join(", ")}`);
      setWgidInput("");
    } finally {
      setAttaching(false);
    }
  };

  const handleDetach = async (wgid) => {
    try {
      await api.post(`/mrss/compilations/${compilation.id}/detach-wgid/`, { wgid });
      setWgids((prev) => prev.filter((w) => w.wgid !== wgid));
      onChanged && onChanged();
    } catch {
      toast.error(`Failed to remove WGID ${wgid}`);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: 3 } } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <VideoLibraryIcon sx={{ color: "#059669", fontSize: 20, flexShrink: 0 }} />
          <Box minWidth={0}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>{compilation.title}</Typography>
            <WcaBadge size="small">{compilation.unique_key}</WcaBadge>
          </Box>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1, mt: 1, mb: 2,
          }}
        >
          <TextField
            size="small" fullWidth label="WGID(s) to attach" value={wgidInput}
            placeholder="Comma separated for several at once"
            onChange={(e) => setWgidInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAttach()}
          />
          <Button
            variant="contained" disableElevation onClick={handleAttach}
            disabled={attaching || !wgidInput.trim()}
            sx={{ borderRadius: 2, textTransform: "none", whiteSpace: "nowrap", minHeight: 40, flexShrink: 0 }}
          >
            {attaching ? <CircularProgress size={16} color="inherit" /> : "Attach"}
          </Button>
        </Box>

        {wgids.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
            No WGIDs attached yet.
          </Typography>
        ) : (
          <Box display="flex" flexDirection="column" gap={1}>
            {wgids.map((w) => (
              <Paper
                key={w.wgid}
                variant="outlined"
                sx={{ p: 1, borderRadius: 2, display: "flex", alignItems: "center", gap: 1, borderColor: "#e2e8f0" }}
              >
                {w.verified ? <VerifiedIcon sx={{ color: "#16a34a", fontSize: 18 }} /> : <Box width={18} />}
                <Box flex={1} minWidth={0}>
                  <Typography variant="body2" fontWeight={600} noWrap>{w.wgid}</Typography>
                  {w.verified_title && (
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                      {w.verified_title}
                    </Typography>
                  )}
                </Box>
                <IconButton size="small" onClick={() => handleDetach(w.wgid)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Paper>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={CONFIRM_ACTIONS_SX}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Watch a compilation's video ──────────────────────────────────────────────
export function PreviewVideoDialog({ open, compilation, onClose }) {
  const fullScreen = useIsPhone();
  if (!compilation) return null;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: 3 } } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, px: { xs: 2, sm: 3 } }}>
        <Box flex={1} minWidth={0}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>{compilation.title}</Typography>
          <Box mt={0.5} display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <WcaBadge size="small">{compilation.unique_key}</WcaBadge>
            <ApprovalBadge status={compilation.compilation_approval_status} />
            <TimeAgo iso={compilation.created_date} prefix="Created " />
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ flexShrink: 0, mt: -0.5 }} aria-label="Close preview">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ bgcolor: "#000", height: { xs: 230, sm: 420 } }}>
          {compilation.video_url ? (
            <VideoPlayer src={compilation.video_url} />
          ) : (
            <Box display="flex" alignItems="center" justifyContent="center" height="100%" color="grey.500">
              <Typography variant="body2">No video available</Typography>
            </Box>
          )}
        </Box>
        {compilation.is_staged && (
          <Alert severity="info" sx={{ borderRadius: 0 }}>
            This is a private preview link — the video isn't publicly reachable until it's approved.
          </Alert>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Reject, with an optional reason ──────────────────────────────────────────
export function RejectCompilationDialog({ open, compilation, onClose, onRejected }) {
  const [reason, setReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  useEffect(() => { if (open) setReason(""); }, [open]);
  if (!compilation) return null;

  const handleConfirm = async () => {
    setRejecting(true);
    try {
      const { data } = await api.post(`/mrss/compilations/${compilation.id}/reject/`, { reason });
      const n = data?.deleted_files?.length || 0;
      toast.success(n ? `${compilation.unique_key} rejected — ${n} file(s) removed` : `${compilation.unique_key} rejected`);
      onRejected && onRejected();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to reject");
    } finally {
      setRejecting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={rejecting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <ThumbDownAltIcon color="error" />
        <Typography variant="h6" fontWeight={700}>Reject Compilation</Typography>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap" minWidth={0}>
          <WcaBadge size="small">{compilation.unique_key}</WcaBadge>
          <Typography variant="body2" fontWeight={600} noWrap sx={{ minWidth: 0, maxWidth: "100%" }}>
            {compilation.title}
          </Typography>
        </Box>
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          The uploaded video is deleted — from the S3 staging area and from this server. It was
          never publicly reachable, so nothing goes offline. The compilation itself stays, with the
          reason below, and can be re-uploaded.
        </Alert>
        <TextField
          fullWidth multiline rows={2} label="Reason (optional)"
          value={reason} onChange={(e) => setReason(e.target.value)}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
        />
      </DialogContent>
      <DialogActions sx={CONFIRM_ACTIONS_SX}>
        <Button onClick={onClose} disabled={rejecting} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button
          variant="contained" color="error" disableElevation onClick={handleConfirm} disabled={rejecting}
          startIcon={rejecting ? <CircularProgress size={16} color="inherit" /> : <ThumbDownAltIcon />}
          sx={{ borderRadius: 2, textTransform: "none" }}
        >
          {rejecting ? "Rejecting…" : "Reject"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Delete for good ──────────────────────────────────────────────────────────
export function DeleteCompilationDialog({ open, compilation, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);

  const handleConfirm = async () => {
    if (!compilation) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/mrss/compilations/${compilation.id}/`);
      const n = data?.deleted_files?.length || 0;
      toast.success(n ? `${compilation.unique_key} deleted — ${n} file(s) removed` : `${compilation.unique_key} deleted`);
      onDeleted && onDeleted();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete compilation");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={deleting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 },
            background: "linear-gradient(135deg, #7f1d1d 0%, #ef4444 100%)",
            color: "white", display: "flex", alignItems: "center", gap: 1.5,
          }}
        >
          <Avatar sx={{ bgcolor: "rgba(255,255,255,0.15)", width: 38, height: 38, flexShrink: 0 }}>
            <WarningAmberIcon />
          </Avatar>
          <Box minWidth={0}>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: 17, sm: 20 } }}>Delete Compilation</Typography>
            <Typography variant="caption" sx={{ opacity: 0.75 }}>This action cannot be undone</Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
          The video file is permanently removed — the S3 object, the staged upload and any local
          copy — along with every attached WGID. If this compilation has already been published to
          a feed, that feed will stop being able to play it.
        </Alert>
        {compilation && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: "#fca5a5", bgcolor: "#fff5f5" }}>
            <Typography variant="caption" color="text.secondary" display="block">Compilation</Typography>
            <Typography variant="body2" fontWeight={700}>{compilation.title}</Typography>
            <Box mt={0.5}><WcaBadge size="small">{compilation.unique_key}</WcaBadge></Box>
          </Paper>
        )}
      </DialogContent>
      <DialogActions sx={CONFIRM_ACTIONS_SX}>
        <Button onClick={onClose} disabled={deleting} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button
          variant="contained" color="error" disableElevation onClick={handleConfirm} disabled={deleting}
          startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
          sx={{ borderRadius: 2, textTransform: "none" }}
        >
          {deleting ? "Deleting…" : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
